import type {
  GrantFmv,
  OperationalAwardTypeCode,
  ShareworksClaimResult,
  ShareworksNotReady,
  ShareworksRow,
  ShareworksWorklist,
  TableSort,
} from '@myrewards/contracts';
import { TYPE_SHORT } from '../../ops/labels';
import { formatCount, formatMoney, plural, type ListFilter, type SortColumn } from '../../ui';

// SA5: every share and option grant to enter in Shareworks, one row per grant (APP-067 §6). A
// person's rows are claimed together, one by one or in a batch, so nobody enters a grant twice; a
// claim lapses after the timeout with no activity. Each grant is checked off on its own ("Entered
// in Shareworks", with who and when), and a person closes once all their grants are entered.

export type RowState = 'yours' | 'open' | 'held';

export function rowState(row: ShareworksRow): RowState {
  if (row.yours) return 'yours';
  return row.claimedBy === null ? 'open' : 'held';
}

export type ShareworksFilter = 'all' | 'open' | 'mine' | 'not_ready' | 'entered';

export const FILTERS: readonly { id: ShareworksFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'open', label: 'Open' },
  { id: 'mine', label: 'Mine' },
  { id: 'not_ready', label: 'Not ready' },
  { id: 'entered', label: 'Entered today' },
];

export function filterFromParam(value: string | null): ShareworksFilter {
  return FILTERS.some((f) => f.id === value) ? (value as ShareworksFilter) : 'all';
}

export function filterCount(list: ShareworksWorklist, filter: ShareworksFilter): number {
  switch (filter) {
    case 'all':
      return list.rows.length;
    case 'open':
      return list.rows.filter((r) => rowState(r) === 'open').length;
    case 'mine':
      return list.rows.filter((r) => r.yours).length;
    case 'not_ready':
      return list.notReady.length;
    case 'entered':
      return list.enteredToday.length;
  }
}

export function visibleRows(list: ShareworksWorklist, filter: ShareworksFilter): ShareworksRow[] {
  switch (filter) {
    case 'open':
      return list.rows.filter((r) => rowState(r) === 'open');
    case 'mine':
      return list.rows.filter((r) => r.yours);
    default:
      return [...list.rows];
  }
}

/** A row's key: a person's line and which grant (both of a person's rows share the line). */
export function grantKey(row: { lineId: string; grant: ShareworksRow['grant'] }): string {
  return `${row.lineId}:${row.grant}`;
}

/** "Options" or "Shares". */
export const GRANT_WORDS: Record<ShareworksRow['grant'], string> = {
  options: 'Options',
  shares: 'Shares',
};

/** Options vest over the setting's years; shares an election bought are owned outright. */
export function vestingWords(row: ShareworksRow): string {
  if (row.grant === 'shares') return 'None, owned outright';
  return row.vestingYears === null ? 'Vesting not set' : plural(row.vestingYears, 'year', 'years');
}

/**
 * A grant's price basis (QA-041): the FMV its award was worked out with, never today's. "$77.95
 * FMV" when it's today's FMV, "$63.10 FMV 2025" (the year that FMV took effect) when it isn't, so
 * the price to copy into Shareworks is on the row. Today's FMV unknown: the year always shows.
 */
export function priceBasis(
  fmv: GrantFmv | null,
  today: { cents: number; effectiveFrom: string | null } | null,
): { text: string; older: boolean } {
  if (fmv === null) return { text: 'Not on the award', older: false };
  const price = `${formatMoney(fmv.cents)} FMV`;
  const isToday =
    today !== null && today.cents === fmv.cents && today.effectiveFrom === fmv.effectiveFrom;
  if (isToday) return { text: price, older: false };
  return {
    text: fmv.effectiveFrom === null ? price : `${price} ${fmv.effectiveFrom.slice(0, 4)}`,
    older: today !== null,
  };
}

/** "64 options", "90 shares". */
export function grantAmount(row: { grant: ShareworksRow['grant']; quantity: number }): string {
  return row.grant === 'options'
    ? plural(row.quantity, 'option', 'options')
    : plural(row.quantity, 'share', 'shares');
}

/** What a grant on its way waits on, in words. */
export function waitingWords(row: ShareworksNotReady): string {
  switch (row.waitingOn) {
    case 'election':
      return 'The employee’s choice';
    case 'payroll':
      return 'Payroll';
    case 'statement_check':
      return 'The statement check';
  }
}

/** A grant on its way: how many, or that the election hasn't chosen yet. */
export function notReadyAmount(row: ShareworksNotReady): string {
  return row.quantity === null
    ? 'Not chosen yet'
    : grantAmount({ grant: row.grant, quantity: row.quantity });
}

/** "Tyler Brandt is working in this list · 3 rows", or nobody. */
export function workingSentence(list: ShareworksWorklist): { title: string; detail: string } {
  const [first, ...others] = list.working;
  if (first === undefined)
    return {
      title: 'Nobody else is working in this list',
      detail: 'When you or a teammate claim rows, you’ll both see who is on which rows here.',
    };
  if (others.length === 0)
    return {
      title: `${first.person.name} is working in this list · ${plural(first.rows, 'row', 'rows')}`,
      detail: 'Their rows are locked for you until they check them off or hand them back.',
    };
  const names = list.working.map((w) => w.person.name);
  return {
    title: `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''} are working in this list`,
    detail: list.working
      .map((w) => `${w.person.name} · ${plural(w.rows, 'row', 'rows')}`)
      .join(' · '),
  };
}

/** After a batch claim: which rows went to someone else, by whom. */
export function takenWords(
  result: ShareworksClaimResult,
  rows: readonly ShareworksRow[],
): string | null {
  if (result.taken.length === 0) return null;
  const nameOf = (lineId: string): string => {
    const row = rows.find((r) => r.lineId === lineId);
    return row === undefined ? 'One row' : `${row.person.name} (${row.award.displayId})`;
  };
  const parts = result.taken.map((t) =>
    t.by === null
      ? `${nameOf(t.lineId)} isn’t waiting any more`
      : `${nameOf(t.lineId)} is with ${t.by}`,
  );
  const claimed =
    result.claimed.length === 0
      ? 'Nothing was claimed.'
      : `You claimed ${plural(result.claimed.length, 'row', 'rows')}.`;
  return `${claimed} ${parts.join('; ')}.`;
}

/**
 * The rows left to enter: every grant not yet checked off. The queue's heroes count the same rows
 * by the same rule on the server (`shareworksRows`, APP-067 §13), so the numbers agree.
 */
export function rowsToEnter(list: ShareworksWorklist): ShareworksWorklist['rows'] {
  return list.rows.filter((r) => r.entered === null);
}

/** "3 open · 2 claimed" under the hero's count. */
export function rowsMeta(list: ShareworksWorklist): string {
  const left = rowsToEnter(list);
  if (left.length === 0) return 'No grants to enter';
  const open = left.filter((r) => rowState(r) === 'open').length;
  return `${formatCount(open)} open · ${formatCount(left.length - open)} claimed`;
}

// --- Sorting and filters (APP-149 D3-D5) ------------------------------------------------------

/** The worklist opens as it does today: the oldest grant date first, ties by award number. */
export const SHAREWORKS_DEFAULT_SORT: TableSort = { column: 'grantDate', dir: 'asc' };
export const SHAREWORKS_DEFAULT_WORDS = 'oldest first';
export const shareworksTie = (row: ShareworksRow): string => row.award.displayId;

/** The worklist's columns that sort: yours first under Claimed by, then the team's, then open. */
export const SHAREWORKS_SORT_COLUMNS: readonly SortColumn<ShareworksRow>[] = [
  { id: 'employee', header: 'Employee', sort: { value: (row) => row.person.name } },
  { id: 'award', header: 'Award', sort: { value: (row) => row.award.displayId } },
  { id: 'grant', header: 'Grant type', sort: { value: (row) => GRANT_WORDS[row.grant] } },
  { id: 'quantity', header: 'Quantity', numeric: true, sort: { value: (row) => row.quantity } },
  {
    id: 'grantDate',
    header: 'Grant date',
    sort: { value: (row) => row.grantDate, words: ['oldest first', 'newest first'] },
  },
  {
    id: 'claimed',
    header: 'Claimed by',
    sort: {
      value: (row) => {
        const state = rowState(row);
        return state === 'yours' ? '0' : state === 'held' ? `1 ${row.claimedBy?.name ?? ''}` : '2';
      },
      words: ['claimed first', 'claimed last'],
    },
  },
];

export type ShareworksFilterId = 'grant' | 'type';

/**
 * The filters that mean something on the worklist (D5), beside its All, Open and Mine: the kind
 * of grant, and the award type. Who holds a row is what All, Open and Mine already choose.
 */
export function shareworksFilters(
  rows: readonly ShareworksRow[],
): Record<ShareworksFilterId, ListFilter<ShareworksRow>> {
  // The award types in their own order (TYPE_SHORT's), only those the worklist holds.
  const types = (Object.keys(TYPE_SHORT) as OperationalAwardTypeCode[]).filter((code) =>
    rows.some((row) => row.type === code),
  );
  return {
    grant: {
      name: 'Grant type',
      options: [
        { value: 'all', label: 'All grants' },
        { value: 'options', label: GRANT_WORDS.options },
        { value: 'shares', label: GRANT_WORDS.shares },
      ],
      passes: (row, value) => row.grant === value,
      listAll: true,
    },
    type: {
      name: 'Type',
      options: [
        { value: 'all', label: 'All types' },
        ...types.map((code) => ({ value: code, label: TYPE_SHORT[code] })),
      ],
      passes: (row, value) => row.type === value,
    },
  };
}

/** The params the worklist keeps in the address: its sort's and its filters'. */
export const SHAREWORKS_PARAMS: readonly string[] = ['sort', 'dir', 'grant', 'type'];
